import React from 'react';
import { useIntl } from 'react-intl';
import { MdAutoAwesome } from 'react-icons/md';
import { EmptyState } from '@aibuild/ui';
import { createAppBuilderConversation, listAppBuilderConversations } from '@api/appBuilder';
import ScopedAssistantChat, { type ScopedAssistantChatProps } from '@components/ScopedAssistantChat';
import { useScopedAssistantConversation } from '@hooks/useScopedAssistantConversation';
import { appBuilderKeys } from './useAppBuilder';

type PassedThrough =
  | 'onBusyChange'
  | 'snapshot'
  | 'onPreviewCapture'
  | 'draft'
  | 'initialSend'
  | 'onInitialSendSettled'
  | 'onProgress'
  | 'controlRef';

interface BuilderChatProps extends Pick<ScopedAssistantChatProps, PassedThrough> {
  readonly appKey: string;
  /** Called when a turn ends, to pull whatever the assistant saved into the draft. */
  readonly onDraftEdited: () => void;
}

/** The App Builder assistant: a chat bound to one app that edits its draft. */
const BuilderChat: React.FC<BuilderChatProps> = ({ appKey, onDraftEdited, ...passed }) => {
  const intl = useIntl();
  const conversation = useScopedAssistantConversation({
    queryKey: appBuilderKeys.conversation(appKey),
    enabled: true,
    list: () => listAppBuilderConversations(appKey),
    create: () => createAppBuilderConversation(appKey),
  });

  return (
    <ScopedAssistantChat
      {...passed}
      conversation={conversation}
      onTurnFinished={onDraftEdited}
      unavailableMessage={intl.formatMessage({
        id: 'apps.builder.chat.unavailable',
        defaultMessage: "The App Builder assistant isn't available for this app.",
      })}
      renderEmptyState={() => (
        <EmptyState
          icon={<MdAutoAwesome size={24} />}
          title={intl.formatMessage({
            id: 'apps.builder.chat.empty.title',
            defaultMessage: 'What should your app do?',
          })}
          description={intl.formatMessage({
            id: 'apps.builder.chat.empty.body',
            defaultMessage: 'Describe the job, the files it takes and what it hands back. The app takes shape beside this chat.',
          })}
          dataTestId="app-builder__chat-empty"
        />
      )}
      placeholder={intl.formatMessage({
        id: 'apps.builder.chat.placeholder',
        defaultMessage: 'Describe the app you want…',
      })}
      dataTestId="app-builder__assistant"
    />
  );
};

export default BuilderChat;
